// Frame composer. Every other module draws a slice of the scene; this file owns
// the order they are painted in, plus screen shake and the impact flash.

import { ctx, W, H } from '../canvas.js';
import { state } from '../state.js';
import { rand } from '../utils.js';
import { SHEEP_FUSE_COUNTDOWN_MS, SHEEP_FUSE_MS } from '../config.js';
import { drawBackground, updateSky } from './background.js';
import { drawBuildings } from './buildings.js';
import { drawFx, updateFx } from './fx.js';
import { drawAim } from './aim.js';
import { drawProps } from './gorilla.js';
import { drawCrates } from '../crates.js';
import { drawGorilla } from './gorilla.js';
import { drawProjectile } from './projectile.js';
import { drawDrone } from './drone.js';
import { drawBubbles } from './banter.js';
import { drawWasted } from './wasted.js';
import { drawAnnouncement } from './announce.js';
import { drawWeather } from './weather.js';
import { drawHud } from './hud.js';

/**
 * The sheep, mid-hop.
 *
 * A ball of wool with a head, four legs and a shadow. It is about 30 px across and crossing a
 * city, so the two things that have to read are "small animal" and "where is it going to
 * land" — and the shadow answers the second one on its own. It leans into the hop with its
 * vertical speed, which is what makes it look thrown rather than slid along.
 *
 * The wool is four circles in one colour, so they union into a single silhouette rather than
 * reading as four circles. Same trick as the bat in background.js, and for the same reason.
 */
function drawSheep() {
  const s = state.sheep;
  if (!s) return;

  // The shadow goes on the roof below it, so it says where the thing is about to come down.
  const roof = state.buildings.find(b => s.x >= b.x && s.x <= b.x + b.w && b.y >= s.y);
  if (roof) {
    ctx.save();
    ctx.globalAlpha = 0.22;
    ctx.fillStyle = '#000';
    ctx.beginPath();
    ctx.ellipse(s.x, roof.y + 2, 15, 4, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  ctx.save();
  // s.y is where the animal touches down — landing sets it to the roof it is standing on — but the
  // sprite runs from -15, its back, to +15, the tips of its legs. Drawn from s.y it sat with its
  // belly inside every roof it bounced off: measured against a parapet, 13 px of wool were below
  // the roof line, and that is what "glitchy when it hits a wall" was. The legs hang below the
  // body, so the feet are 15 and the body is drawn from the feet up.
  const FEET = 15;
  ctx.translate(s.x, s.y - FEET);
  ctx.rotate(Math.max(-0.5, Math.min(0.5, s.vy / 24)) * s.dir);

  ctx.fillStyle = '#2b2f3a';
  [-9, -3, 4, 10].forEach(dx => ctx.fillRect(dx, 8, 3, 7));

  ctx.fillStyle = '#f4f1e6';
  [[-8, 0, 9], [8, 0, 9], [0, -5, 10], [0, 4, 9]].forEach(([dx, dy, r]) => {
    ctx.beginPath();
    ctx.arc(dx, dy, r, 0, Math.PI * 2);
    ctx.fill();
  });

  ctx.fillStyle = '#2b2f3a';
  ctx.beginPath();
  ctx.ellipse(14 * s.dir, -2, 6, 5, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();

  drawSheepFuse();
}

/**
 * The last five seconds, counted over the sheep's head.
 *
 * The fuse is twelve seconds and the only clock the player has is a bleat per hop, which is
 * plenty while the animal is still walking and useless at the moment it matters: four seconds
 * and one second are the difference between moving out of the way and wearing it. Whole
 * seconds, because nobody reads tenths off a sheep.
 *
 * Drawn in world coordinates and NOT inside the hop's own transform: the sprite leans into the
 * hop by up to half a radian, and a number that leans with it is a number nobody can read at
 * the one moment they are looking at it. Measured over two and a half seconds of hopping, the
 * counter rides between y=140 and y=310, so it has room above it for whatever the sheep does.
 */
function drawSheepFuse() {
  const s = state.sheep;
  if (!s) return;

  const left = SHEEP_FUSE_MS - (state.clock - s.startAt);
  if (left <= 0 || left > SHEEP_FUSE_COUNTDOWN_MS) return;

  // The last second changes colour, and to the same one the turn clock turns when a turn is
  // running out: in this game that colour already means "this is about to go off".
  const urgent = left <= 1000;
  const x = Math.round(s.x);
  const y = Math.round(s.y) - 15 - 21;

  ctx.save();
  ctx.font = 'bold 15px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // A dark edge around it, because it is drawn over a neon skyline and over rooftops and the
  // wool itself, and a pale digit on pale wool is no countdown at all.
  ctx.lineWidth = 3;
  ctx.strokeStyle = '#0b0e18';
  ctx.strokeText(String(Math.ceil(left / 1000)), x, y);
  ctx.fillStyle = urgent ? '#ff8a6a' : '#fff2c4';
  ctx.fillText(String(Math.ceil(left / 1000)), x, y);
  ctx.restore();
}

const SHAKE_DECAY = 0.85;
const SHAKE_CUTOFF = 0.5;
const FLASH_DECAY = 0.06;

export function draw() {
  ctx.save();

  if (state.shake > 0) {
    ctx.translate(rand(-state.shake, state.shake), rand(-state.shake, state.shake));
    state.shake *= SHAKE_DECAY;
    if (state.shake < SHAKE_CUTOFF) state.shake = 0;
  }

  drawBackground();
  drawBuildings();
  // Behind the gorillas: a crate sits on a roof in the scene, it is not carried.
  drawCrates();
  drawFx();
  drawAim();
  state.players.forEach(drawGorilla);
  // After the gorillas and not with them: a shield, a spring and a mushroom are
  // all in front of whoever is carrying them, so they have to be painted over him.
  drawProps();
  drawProjectile();
  drawSheep();
  drawDrone();
  // Over the scene, so the rain reads as falling between you and the fight.
  drawWeather();

  // No HUD on the front-end: the title screen is an attract mode, and score
  // panels on it would make it look like a paused match instead.
  if (state.screen === 'game' || state.screen === 'pause') drawHud();

  // And the bubbles last of all. A gorilla that has gone to the trouble of
  // saying something is going to be read, even if it is standing in a mushroom
  // cloud at the time.
  drawBubbles();

  ctx.restore();

  // The kill caption, then the flash on top of it: the blast punches through the
  // caption rather than being covered by it.
  drawWasted();

  // And the round announcement last of all, over even the caption. It is the only
  // thing in the game that speaks to the player rather than to the fight.
  drawAnnouncement();

  if (state.flash > 0) {
    ctx.fillStyle = `rgba(255,240,210,${state.flash})`;
    ctx.fillRect(0, 0, W, H);
    state.flash -= FLASH_DECAY;
  }

  updateFx();
  updateSky();
}
